import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { useTranslation } from "react-i18next";
import posService from "../../services/pos/pos.service";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import { TieredMenu } from "primereact/tieredmenu";
import { MenuItem } from "primereact/menuitem";
import ViewBatchesDialog from "./ViewBatchesDialog";
import InwardDialog from "./InwardDialog";
import OutwardDialog from "./OutwardDialog";
import AdjustDialog from "./AdjustDialog";
import RequestDialog from "./RequestDialog";
import { ProductStockSummary } from "../../models/pos/product-stock-summary-model";


interface DataTableProps {
  data: ProductStockSummary[];
  onRefresh: (posIds: number[]) => void;
  setLoading: (loading: boolean) => void;
  selectedPosIds: number[];
}

const PosStockSummaryDatatable: React.FC<DataTableProps> = ({
  data,
  onRefresh,
  setLoading,
  selectedPosIds,
}) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const menuRefs = useRef<{ [key: string]: TieredMenu | null }>({});  
  const [selectedProduct, setSelectedProduct] = useState<ProductStockSummary | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editSafetyStock, setEditSafetyStock] = useState<number>(0);
  const [editReorderStock, setEditReorderStock] = useState<number>(0);
  const [showViewBatchesDialog, setShowViewBatchesDialog] = useState(false);
  const [showInwardDialog, setShowInwardDialog] = useState(false);
  const [showOutwardDialog, setShowOutwardDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);


  const showMessage = (summary: string, detail: string, severity: any) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };

  const handleEditClick = (product: ProductStockSummary) => {
    setSelectedProduct(product);
    setEditSafetyStock(product.safetyStock);
    setEditReorderStock(product.reorderStock);
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedProduct) return;

    if (editSafetyStock < 0 || editReorderStock < 0) {
      showMessage("Validation Error", "Safety Stock and Reorder Stock must be positive values", "warn");
      return;
    }

    // if (editReorderStock > editSafetyStock) {
    //   showMessage("Validation Error", "Reorder Stock cannot be greater than Safety Stock", "warn");
    //   return;
    // }

    try {
      setLoading(true);
      const payload = {
        configId: selectedProduct.configId,
        safetyStock: editSafetyStock,
        reorderStock: editReorderStock,
      };

      const response = await posService.updateProductStockConfig(payload);

      if (response?.isSuccess || response?.success) {
        showMessage("Success", "Product stock config updated successfully", "success");
        setShowEditDialog(false);
        onRefresh(selectedPosIds);
      } else {
        showMessage("Error", response?.message || "Failed to update product stock config", "error");
      }
    } catch (error) {
      console.error("Error updating product stock config:", error);
      showMessage("Error", "An error occurred while updating product stock config", "error");
    } finally {
      setLoading(false);
    }
  };

  const statusBodyTemplate = (product: ProductStockSummary) => {
    let severity: "success" | "secondary" | "info" | "warning" | "danger" | "contrast" = "secondary";
    if (product.status === "Sufficient") severity = "success";
    else if (product.status === "Low") severity = "warning";
    else if (product.status === "Critical") severity = "danger";

    return <span className={`p-tag p-tag-${severity}`}>{product.status}</span>;
  };

  // const actionBodyTemplate = (product: ProductStockSummary) => {
  //   return (
  //     <Button
  //       icon="pi pi-pencil"
  //       className="p-button-rounded p-button-text"
  //       onClick={() => handleEditClick(product)}
  //       tooltip="Edit Product Stock Config"
  //       tooltipOptions={{ position: "left" }}
  //     />
  //   );
  // };
  const actionBodyTemplate = (product: ProductStockSummary) => {
    const menuKey = `${product.productId}-${product.posId}`;
    const menuItems: MenuItem[] = [
      {
        label: 'Inward',
        icon: 'pi pi-arrow-down',
        command: () => {
          setSelectedProduct(product);
          setShowInwardDialog(true);
          console.log('Inward clicked for', product.productName, 'POS ID:', product.posId);
        }
      },
      {
        label: 'Outward',
        icon: 'pi pi-arrow-up',
        command: () => {
          setSelectedProduct(product);
          setShowOutwardDialog(true);
          console.log('Outward clicked for', product.productName, 'POS ID:', product.posId);
        }
      },
      {
        label: 'Adjust',
        icon: 'pi pi-sliders-h',
        command: () => {
          setSelectedProduct(product);
          setShowAdjustDialog(true);
          console.log('Adjust clicked for', product.productName, 'POS ID:', product.posId);
        }
      },
      {
        label: 'Request',
        icon: 'pi pi-send',
        command: () => {
          setSelectedProduct(product);
          setShowRequestDialog(true);
          console.log('Request clicked for', product.productName, 'POS ID:', product.posId);
        }
      },
      {
        separator: true
      },
      {
        label: 'View Batches',
        icon: 'pi pi-list',
        command: () => {
          setSelectedProduct(product);
          setShowViewBatchesDialog(true);
          console.log('View Batches clicked for', product.productName, 'POS ID:', product.posId);
        }
      },
      {
        separator: true
      },
      {
        label: 'Edit Config',
        icon: 'pi pi-pencil',
        command: () => {
          setSelectedProduct(product);
          setEditSafetyStock(product.safetyStock);
          setEditReorderStock(product.reorderStock);
          setShowEditDialog(true);
        }
      }
    ];

    return (
      <>
        <TieredMenu 
          model={menuItems} 
          popup 
          ref={(el) => {
            if (el) menuRefs.current[menuKey] = el;
          }}
          id={`menu-${menuKey}`}
        />
        <Button
          icon="pi pi-ellipsis-v"
          className="p-button-rounded p-button-text"
          onClick={(e) => {
            menuRefs.current[menuKey]?.toggle(e);
          }}
          tooltip="Actions"
          tooltipOptions={{ position: "left" }}
        />
      </>
    );
  };





  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="col-12">
        <div className="card">
          {data && data.length > 0 ? (
            <>
            <p className="text-sm text-color-secondary mt-0">Open Order Demand includes pending quantities from the last 30 days.</p>
            <DataTable
              value={data}
              scrollable
              tableStyle={{ minWidth: "75rem" }}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 25, 50, 100]}
              paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
                className="p-datatable-striped"
            >
              <Column field="productName" header="Product Name" sortable style={{ width: "20%" }} />
              <Column field="posName" header="POS Name" sortable style={{ width: "15%" }} />
              {/* <Column field="stock" header="Stock" sortable style={{ width: "10%" }} /> */}
              <Column field="availableStock" header="Available Stock" sortable style={{ width: "12%" }} />
              <Column field="openOrderDemand" header="Open Order Demand" sortable
                body={(product: ProductStockSummary) => product.openOrderDemand ?? "—"} style={{ minWidth: "10rem" }} />
              <Column field="availableStockForOrder" header="Available Stock for Order" sortable
                body={(product: ProductStockSummary) => product.availableStockForOrder ?? "—"}
                headerStyle={{ backgroundColor: "#dbeafe", color: "#1e40af", borderInline: "1px solid #93c5fd" }}
                bodyStyle={{ backgroundColor: "#eff6ff", color: "#1e3a8a", fontWeight: 700,
                  fontVariantNumeric: "tabular-nums", borderInline: "1px solid #bfdbfe" }}
                style={{ minWidth: "12rem" }} />
              <Column field="measurementType" header="Measurement Type" sortable style={{ width: "12%" }} />
              <Column field="safetyStock" header="Safety Stock" sortable style={{ width: "10%" }} />
              <Column field="reorderStock" header="Reorder Stock" sortable style={{ width: "10%" }} />
              <Column
                field="status"
                header="Status"
                body={statusBodyTemplate}
                sortable
                style={{ width: "10%" }}
              />
              {/* <Column 
                header="Stock Actions" 
                body={stockActionBodyTemplate} 
                style={{ width: "12%" }} 
              /> */}
              <Column body={actionBodyTemplate} style={{ width: "8%" }} />
            </DataTable>
            </>
          ) : (
            <div className="text-center p-4">
              <p className="text-gray-500">No products found. Please select a POS to view the product stock summary.</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog
        visible={showEditDialog}
        onHide={() => setShowEditDialog(false)}
        header="Edit Product Stock Config"
        modal
        className="p-fluid"
        style={{ width: "30vw" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
      >
        {selectedProduct && (
          <div>
            <div className="field mb-3">
              <label htmlFor="productName" className="font-semibold text-sm block mb-1">
                Product Name
              </label>
              <InputText
                id="productName"
                disabled
                value={selectedProduct.productName}
                className="w-full"
                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
              />
            </div>

            <div className="field mb-3">
              <label htmlFor="posName" className="font-semibold text-sm block mb-1">
                POS Name
              </label>
              <InputText
                id="posName"
                disabled
                value={selectedProduct.posName}
                className="w-full"
                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
              />
            </div>

            <div className="field mb-3">
              <label htmlFor="safetyStock" className="font-semibold text-sm block mb-1">
                Safety Stock <span className="text-red-500">*</span>
              </label>
              <InputNumber
                id="safetyStock"
                value={editSafetyStock}
                onValueChange={(e) => setEditSafetyStock(e.value || 0)}
                placeholder="Enter safety stock"
                min={0}
                useGrouping={false}
                className="w-full"
                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
              />
            </div>

            <div className="field mb-3">
              <label htmlFor="reorderStock" className="font-semibold text-sm block mb-1">
                Reorder Stock <span className="text-red-500">*</span>
              </label>
              <InputNumber
                id="reorderStock"
                value={editReorderStock}
                onValueChange={(e) => setEditReorderStock(e.value || 0)}
                placeholder="Enter reorder stock"
                min={0}
                useGrouping={false}
                className="w-full"
                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
              />
            </div>

            <div className="flex gap-2 justify-content-end">
              <Button
                label="Cancel"
                onClick={() => setShowEditDialog(false)}
                severity="secondary"
                className="text-black border-round p-button-m"
              />
              <Button label="Save" onClick={handleSaveEdit} />
            </div>
          </div>
        )}
      </Dialog>

      {/* Inward Dialog */}
      {selectedProduct && (
      <InwardDialog
        visible={showInwardDialog}
        onHide={() => setShowInwardDialog(false)}
        productId={selectedProduct.productId}
        posId={selectedProduct.posId}
        productName={selectedProduct.productName}
        posName={selectedProduct.posName}
        minOrderQty={selectedProduct.minOrderQty}
        onSuccess={() => onRefresh(selectedPosIds)}
      />
    )}

    {/* Outward Dialog */}
{selectedProduct && (
  <OutwardDialog
    visible={showOutwardDialog}
    onHide={() => setShowOutwardDialog(false)}
    productId={selectedProduct.productId}
    posId={selectedProduct.posId}
    productName={selectedProduct.productName}
    posName={selectedProduct.posName}
    minOrderQty={selectedProduct.minOrderQty}
    onSuccess={() => onRefresh(selectedPosIds)}
  />
)}

{/* Adjust Dialog */}
{selectedProduct && (
  <AdjustDialog
    visible={showAdjustDialog}
    onHide={() => setShowAdjustDialog(false)}
    productId={selectedProduct.productId}
    posId={selectedProduct.posId}
    productName={selectedProduct.productName}
    posName={selectedProduct.posName}
    minOrderQty={selectedProduct.minOrderQty}
    onSuccess={() => onRefresh(selectedPosIds)}
  />
)}
{/* Request Dialog */}
{selectedProduct && (
  <RequestDialog
    visible={showRequestDialog}
    onHide={() => setShowRequestDialog(false)}
    productId={selectedProduct.productId}
    posId={selectedProduct.posId}
    productName={selectedProduct.productName}
    posName={selectedProduct.posName}
    minOrderQty={selectedProduct.minOrderQty}
    onSuccess={() => onRefresh(selectedPosIds)}
  />
)}

       {/* View Batches Dialog */}
    {selectedProduct && (
      <ViewBatchesDialog
        visible={showViewBatchesDialog}
        onHide={() => setShowViewBatchesDialog(false)}
        productId={selectedProduct.productId}
        posId={selectedProduct.posId}
        productName={selectedProduct.productName}
      />
    )}
    </>
  );
};

export default PosStockSummaryDatatable;
